import React, { useState } from 'react';
import { BookOpen, CircleCheck, Eye, FileQuestion, FileText, Layers, LoaderCircle, Minus, MessageSquareText, Wand2 } from 'lucide-react';
import { Button } from '../ui';
import { Markdown } from '../ui/Markdown';
import { aiErrorInfo, aiService } from '../../services/api';
import { AIState, stateOfError } from './AIStates';
import './LessonDraft.css';

const PARTS = [
  { key: 'SUMMARY', icon: FileText, label: 'Resumo', hint: '5 a 8 pontos, com termos da prova em negrito' },
  { key: 'CONTENT', icon: BookOpen, label: 'Conteúdo em markdown', hint: 'Seções, exemplos e blocos de código' },
  { key: 'QUESTIONS', icon: FileQuestion, label: '10 questões para o banco', hint: 'Estilo certificação, com explicação', count: 10 },
  { key: 'FLASHCARDS', icon: Layers, label: 'Flashcards', hint: 'Pares pergunta/resposta curtos', count: 10 },
];

/**
 * Admin: gera resumo, conteúdo, questões e flashcards a partir da transcrição (2e).
 * Tudo chega como rascunho; nada é publicado sem o admin revisar.
 */
export function LessonDraft({ lesson, onPublished }) {
  const [selected, setSelected] = useState({ SUMMARY: true, CONTENT: true, QUESTIONS: true, FLASHCARDS: false });
  const [state, setState] = useState({});
  const [drafts, setDrafts] = useState({});
  const [reviewing, setReviewing] = useState(null);
  const [error, setError] = useState(null);
  const words = (lesson.transcription || '').split(/\s+/).filter(Boolean).length;

  const generate = async () => {
    setError(null);
    const keys = PARTS.filter(p => selected[p.key]).map(p => p.key);
    // Uma parte por vez, mostrando o progresso de cada uma
    for (const key of keys) {
      setState(s => ({ ...s, [key]: 'loading' }));
      try {
        const part = PARTS.find(p => p.key === key);
        // eslint-disable-next-line no-await-in-loop
        const r = await aiService.lessonDraft(lesson.id, key, part.count);
        setDrafts(d => ({ ...d, [key]: r }));
        setState(s => ({ ...s, [key]: 'ready' }));
      } catch (err) {
        const info = aiErrorInfo(err);
        setState(s => ({ ...s, [key]: 'error' }));
        setError(info);
        if (stateOfError(info.code) !== 'ERROR') break;
      }
    }
  };

  const ready = PARTS.filter(p => state[p.key] === 'ready' && !drafts[p.key]?.published);
  const loading = PARTS.filter(p => state[p.key] === 'loading').length;
  const busy = loading > 0;

  const publish = async (key, body) => {
    try {
      await aiService.publishLessonDraft(lesson.id, { part: key, ...body });
      setDrafts(d => ({ ...d, [key]: { ...d[key], published: true } }));
      setReviewing(null);
      onPublished?.(key, body);
    } catch (err) {
      setError(aiErrorInfo(err));
    }
  };

  if (!lesson.transcription) {
    return (
      <div className="ldr-empty">
        <MessageSquareText size={20} aria-hidden="true" />
        <span>Adicione a transcrição da aula para gerar resumo, conteúdo, questões e flashcards com IA.</span>
      </div>
    );
  }

  return (
    <div className="ldr">
      <section className="ldr-card">
        <header className="ldr-card__head">
          <span className="ldr-card__icon"><Wand2 size={18} aria-hidden="true" /></span>
          <span className="ldr-card__title">
            <strong>Gerar a partir da transcrição</strong>
            <span>Escolha o que gerar. Tudo chega como rascunho; nada é publicado sem você revisar.</span>
          </span>
          <span className="ldr-badge"><MessageSquareText size={12} aria-hidden="true" />Transcrição · {words.toLocaleString('pt-BR')} palavras</span>
        </header>
        <ul className="ldr-parts">
          {PARTS.map(p => {
            const st = state[p.key];
            const done = drafts[p.key]?.published;
            return (
              <li key={p.key}>
                <input type="checkbox" checked={!!selected[p.key]} disabled={busy}
                       onChange={(e) => setSelected(s => ({ ...s, [p.key]: e.target.checked }))} aria-label={p.label} />
                <span className="ldr-parts__icon"><p.icon size={15} aria-hidden="true" /></span>
                <span className="ldr-parts__text"><strong>{p.label}</strong><span>{p.hint}</span></span>
                <span className={`ldr-parts__state is-${done ? 'done' : st || (selected[p.key] ? 'idle' : 'off')}`}>
                  {done ? <><CircleCheck size={14} aria-hidden="true" />Publicado</>
                    : st === 'loading' ? <><LoaderCircle size={14} className="ldr-spin" aria-hidden="true" />Gerando...</>
                      : st === 'ready' ? <button type="button" onClick={() => setReviewing(p.key)}><CircleCheck size={14} aria-hidden="true" />Pronto</button>
                        : st === 'error' ? 'Falhou'
                          : selected[p.key] ? '' : <><Minus size={14} aria-hidden="true" />Não selecionado</>}
                </span>
              </li>
            );
          })}
        </ul>
        {error && <div className="ldr-error"><AIState kind={stateOfError(error.code)} message={error.message} onRetry={generate} compact /></div>}
        <footer className="ldr-card__foot">
          <span>{ready.length} {ready.length === 1 ? 'pronto' : 'prontos'} para revisar{loading ? ` · ${loading} gerando` : ''}</span>
          {ready.length > 0 && !busy ? (
            <Button icon={Eye} onClick={() => setReviewing(ready[0].key)}>Revisar rascunhos</Button>
          ) : (
            <Button icon={Wand2} onClick={generate} disabled={busy || !Object.values(selected).some(Boolean)}>
              {busy ? 'Gerando...' : 'Gerar rascunhos'}
            </Button>
          )}
        </footer>
      </section>

      {reviewing && drafts[reviewing] && (
        <DraftReview key={reviewing} part={reviewing} draft={drafts[reviewing]} onDiscard={() => setReviewing(null)} onPublish={publish} />
      )}
    </div>
  );
}

function DraftReview({ part, draft, onDiscard, onPublish }) {
  const meta = PARTS.find(p => p.key === part);
  const [md, setMd] = useState(draft.markdown || '');
  const [editing, setEditing] = useState(false);
  const [view, setView] = useState('new');
  const [items, setItems] = useState(draft.questions || draft.cards || []);
  const replaces = (part === 'SUMMARY' || part === 'CONTENT') && draft.current;

  const publishBody = part === 'QUESTIONS' ? { questions: items } : part === 'FLASHCARDS' ? { cards: items } : { markdown: md };
  return (
    <section className="ldr-card ldr-review">
      <header className="ldr-review__head">
        <span className="ldr-eyebrow">{meta.label} · rascunho</span>
        {replaces && <span className="ldr-pill">Substitui o atual</span>}
        {replaces && (
          <div className="ss-seg ldr-review__seg" role="tablist" aria-label="Versão">
            {[['current', 'Atual'], ['new', 'Novo']].map(([v, l]) => (
              <button key={v} type="button" role="tab" aria-selected={view === v} className={`ss-seg__item ${view === v ? 'is-active' : ''}`} onClick={() => setView(v)}>{l}</button>
            ))}
          </div>
        )}
      </header>
      <div className="ldr-review__body">
        {(part === 'SUMMARY' || part === 'CONTENT') && (
          view === 'current' ? <Markdown className="ldr-md">{draft.current}</Markdown>
            : editing ? <textarea className="ss-textarea" rows={part === 'CONTENT' ? 24 : 12} value={md} onChange={(e) => setMd(e.target.value)} />
              : <Markdown className="ldr-md">{md}</Markdown>
        )}
        {part === 'QUESTIONS' && (
          <ol className="ldr-questions">
            {items.map((q, i) => (
              <li key={i}>
                <strong>{q.statement}</strong>
                <ul>{q.options.map((o, k) => <li key={k} className={q.correct.includes(String.fromCharCode(65 + k)) ? 'is-correct' : ''}><b>{String.fromCharCode(65 + k)}</b>{o}</li>)}</ul>
                <span>{q.explanation}</span>
                <button type="button" className="ldr-remove" onClick={() => setItems(list => list.filter((_, j) => j !== i))}>Tirar esta</button>
              </li>
            ))}
          </ol>
        )}
        {part === 'FLASHCARDS' && (
          <ul className="ldr-cards">
            {items.map((c, i) => (
              <li key={i}>
                <strong>{c.front}</strong><span>{c.back}</span>
                <button type="button" className="ldr-remove" onClick={() => setItems(list => list.filter((_, j) => j !== i))}>Tirar</button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <footer className="ldr-review__foot">
        <Button variant="ghost" onClick={onDiscard}>Descartar</Button>
        {(part === 'SUMMARY' || part === 'CONTENT') && (
          <Button variant="outline" onClick={() => { setEditing(e => !e); setView('new'); }}>{editing ? 'Ver prévia' : 'Editar'}</Button>
        )}
        <Button variant="accent" onClick={() => onPublish(part, publishBody)}
                disabled={(part === 'QUESTIONS' || part === 'FLASHCARDS') ? items.length === 0 : !md.trim()}>
          {part === 'SUMMARY' ? 'Publicar resumo' : part === 'CONTENT' ? 'Publicar conteúdo'
            : part === 'QUESTIONS' ? `Pôr ${items.length} no banco` : `Adicionar ${items.length} ao tópico`}
        </Button>
      </footer>
    </section>
  );
}
